import React, { useEffect, useMemo, useState } from 'react';
import {
  ProfileHeroCard,
  AssignmentCards,
  RoutePlanCard,
  SettingsList,
  MonthPerformanceCard,
  LogoutButton,
} from './components';
import {
  SalesRepProfile as SalesRepProfileType,
  AssignedTerritory,
  EmployeeInfo,
  ProfileRouteStop,
  PerformanceStat,
  defaultSettingsItems,
} from './profileTypes';
import { useAuth } from '../../../contexts/AuthContext';
import { ChangePasswordModal } from '../../../components/ChangePasswordModal';
import { useSalesGoalsStore } from '../../../store/salesGoalsStore';
import { useSalesActivitiesStore } from '../../../store/salesActivitiesStore';
import styles from './SalesRepProfile.module.css';

function fmtMoney(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}k`;
  return `$${Math.round(n)}`;
}

const SalesRepProfile: React.FC = () => {
  const { user } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const progress = useSalesGoalsStore((s) => s.progress);
  const fetchProgress = useSalesGoalsStore((s) => s.fetchProgress);
  const activities = useSalesActivitiesStore((s) => s.items);
  const fetchActivities = useSalesActivitiesStore((s) => s.fetchList);

  useEffect(() => {
    fetchProgress();
    fetchActivities({ limit: 50 });
  }, [fetchProgress, fetchActivities]);

  // All fields show the admin-configured value from /users/me (migration 027);
  // any column that's still NULL renders 'N/A'.
  const profile: SalesRepProfileType = useMemo(() => ({
    staffName: user?.full_name ?? 'N/A',
    role: user?.role ?? 'Sales Rep',
    email: user?.email ?? 'N/A',
    phone: user?.phone || 'N/A',
  }), [user]);

  const territory: AssignedTerritory = { name: user?.territory || 'N/A' };

  const employee: EmployeeInfo = useMemo(() => ({
    staffId: user?.employee_id || 'N/A',
    startedSince: 'N/A',
  }), [user]);

  const today0 = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  const routeStops: ProfileRouteStop[] = useMemo(() =>
    activities
      .filter((a) => {
        if (!a.follow_up_at) return false;
        const d = new Date(a.follow_up_at);
        return d >= today0 && d < new Date(today0.getTime() + 86_400_000);
      })
      .slice(0, 6)
      .map((a, i) => {
        const d = new Date(a.follow_up_at!);
        return {
          id: String(a.id),
          index: i + 1,
          name: a.customer_name || a.subject,
          time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
          status: d < new Date() ? 'Completed' : 'Pending',
        };
      })
  , [activities, today0]);

  const performance: PerformanceStat[] = useMemo(() => [
    { id: 'rev', label: 'Revenue', value: fmtMoney(progress?.achieved_revenue ?? 0) },
    { id: 'orders', label: 'Orders', value: String(progress?.achieved_orders ?? 0) },
    { id: 'target', label: 'Target', value: `${progress?.achieved_revenue_pct ?? 0}%` },
  ], [progress]);

  return (
    <div className={styles.container}>
      <ProfileHeroCard profile={profile} />
      <AssignmentCards territory={territory} employee={employee} />
      <RoutePlanCard stops={routeStops} />
      <SettingsList items={defaultSettingsItems} />
      <MonthPerformanceCard stats={performance} />
      <button
        type="button"
        className={styles.securityButton}
        onClick={() => setShowPasswordModal(true)}
      >
        Change Password
      </button>
      <LogoutButton />
      <p className={styles.version}>VIN pulse OS v2.1.0</p>

      <ChangePasswordModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
};

export const SalesRepProfilePage: React.FC = () => {
  return <SalesRepProfile />;
};

export { SalesRepProfile };
export default SalesRepProfile;
